/**
 * 客戶篩選工具列元件 (CustomerFilterBar)
 * 
 * 職責：
 * - 提供搜尋框、會員類別下拉、狀態下拉的篩選 UI
 * - 處理篩選條件變更並通知父元件
 * - 顯示當前篩選狀態
 * 
 * @module CustomerFilterBar
 */

import type { MemberLevel, CustomerStatus } from '../../../../modules/customer/types';
import { levelConfig, statusConfig } from '../../../../modules/customer/constants';

/**
 * CustomerFilterBar Props 介面
 */
interface CustomerFilterBarProps {
  /** 搜尋關鍵字 */
  searchTerm: string;
  
  /** 會員等級篩選 */
  filterLevel: MemberLevel | 'all';
  
  /** 狀態篩選 */
  filterStatus: CustomerStatus | 'all';
  
  /** 搜尋變更回調 */
  onSearchChange: (value: string) => void;
  
  /** 會員等級變更回調 */
  onLevelChange: (value: MemberLevel | 'all') => void;
  
  /** 狀態變更回調 */
  onStatusChange: (value: CustomerStatus | 'all') => void;
}

/**
 * 客戶篩選工具列元件
 */
export function CustomerFilterBar({
  searchTerm,
  filterLevel,
  filterStatus,
  onSearchChange,
  onLevelChange,
  onStatusChange
}: CustomerFilterBarProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 搜尋框 */}
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-lg"></i>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="搜尋客戶姓名、公司、電話、Email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              title="清除搜尋"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          )}
        </div>

        {/* 會員類別篩選 */}
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
            <i className="ri-vip-crown-line text-lg"></i>
          </div>
          <select
            value={filterLevel}
            onChange={(e) => onLevelChange(e.target.value as MemberLevel | 'all')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm appearance-none bg-white cursor-pointer"
          >
            <option value="all">全部會員類別</option>
            {Object.entries(levelConfig).map(([key, config]) => (
              <option key={key} value={key}>
                {config.icon} {config.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 pointer-events-none">
            <i className="ri-arrow-down-s-line text-lg"></i>
          </div>
        </div>

        {/* 狀態篩選 */}
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
            <i className="ri-shield-check-line text-lg"></i>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => onStatusChange(e.target.value as CustomerStatus | 'all')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm appearance-none bg-white cursor-pointer"
          >
            <option value="all">全部狀態</option>
            {Object.entries(statusConfig).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 pointer-events-none">
            <i className="ri-arrow-down-s-line text-lg"></i>
          </div>
        </div>
      </div>

      {/* 篩選狀態提示 */}
      {(searchTerm || filterLevel !== 'all' || filterStatus !== 'all') && (
        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">當前篩選：</span>
          
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
              搜尋: {searchTerm}
              <button
                onClick={() => onSearchChange('')}
                className="w-4 h-4 flex items-center justify-center hover:bg-teal-200 rounded-full transition-colors"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </span>
          )}
          
          {filterLevel !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
              {levelConfig[filterLevel].icon} {levelConfig[filterLevel].label}
              <button
                onClick={() => onLevelChange('all')}
                className="w-4 h-4 flex items-center justify-center hover:bg-teal-200 rounded-full transition-colors"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </span>
          )}
          
          {filterStatus !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
              {statusConfig[filterStatus].label}
              <button
                onClick={() => onStatusChange('all')}
                className="w-4 h-4 flex items-center justify-center hover:bg-teal-200 rounded-full transition-colors"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </span>
          )}
          
          <button
            onClick={() => {
              onSearchChange('');
              onLevelChange('all');
              onStatusChange('all');
            }}
            className="ml-auto text-xs text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap"
          >
            清除全部篩選
          </button>
        </div>
      )}
    </div>
  );
}
